import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import WebApp from '@twa-dev/sdk';
import { ordinaryApi, SESSION_CHANGED, clearOrdinarySession, getOrdinaryToken, installOrdinarySession } from '../security/ordinarySession';
interface TelegramUser { id: string; telegram_id: number; first_name: string; last_name?: string; username?: string; }
interface AuthContextType {
  user: TelegramUser | null; token: string | null; isLoading: boolean; isAuthenticated: boolean;
  isTelegram: boolean; error: string | null; login: () => Promise<void>; logout: () => Promise<void>;
  recover: (code: string) => Promise<void>; logoutAll: () => Promise<void>;
}
const TelegramAuthContext = createContext<AuthContextType | undefined>(undefined);
export const TelegramAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user,setUser]=useState<TelegramUser|null>(null);
  const [token,setToken]=useState<string|null>(null);
  const [isLoading,setLoading]=useState(true);
  const [error,setError]=useState<string|null>(null);
  const [expiresAt,setExpiresAt]=useState(0);
  const generation=useRef(0);
  const isTelegram=!!WebApp.initData;
  const clear=useCallback(() => {generation.current+=1;clearOrdinarySession();setExpiresAt(0);setUser(null);setToken(null);},[]);
  const accept=useCallback(async (data: any,startedAt:number,attempt:number) => {
    if (!data.access_token || !data.user?.id || !Number.isInteger(data.expires_in) || data.expires_in<=0 || data.expires_in>900) throw new Error('Сервер вернул неверную сессию');
    const result=await ordinaryApi.get('/api/v1/auth/identity',{headers:{Authorization:`Bearer ${data.access_token}`}});
    if (!result.data.user_sid || result.data.id!==data.user.id) throw new Error('Сессия не подтверждена сервером');
    if (generation.current!==attempt) return;
    const end=installOrdinarySession(data.access_token,data.expires_in,startedAt);
    setExpiresAt(end);setToken(data.access_token);setUser(data.user);setError(null);
  },[]);
  const authenticate=useCallback(async (path:string,body:object) => {
    clear();const attempt=generation.current;const startedAt=Date.now();setLoading(true);setError(null);
    try {const response=await ordinaryApi.post(path,body);await accept(response.data,startedAt,attempt);}
    catch {if (generation.current===attempt) {clear();setError('Не удалось войти. Откройте приложение заново в Telegram или используйте действующий одноразовый код.');}}
    finally {if (generation.current===attempt || !getOrdinaryToken()) setLoading(false);}
  },[clear,accept]);
  const login=useCallback(async () => {
    if (!WebApp.initData) {clear();setLoading(false);setError('Откройте приложение через Telegram или введите код восстановления.');return;}
    await authenticate('/api/v1/auth/telegram-login',{initData:WebApp.initData});
    WebApp.ready();
  },[clear,authenticate]);
  const recover=useCallback(async (code:string) => authenticate('/api/v1/auth/recover',{recovery_code:code,device_name:'Browser'}),[authenticate]);
  const endSession=useCallback(async (all:boolean) => {
    const current=getOrdinaryToken();clear();setError(null);
    if (!current) return;
    try {await ordinaryApi.post('/api/v1/auth/'+(all?'logout-all':'logout'),null,{headers:{Authorization:`Bearer ${current}`}});}
    catch {setError('Локальный выход выполнен, но сервер не подтвердил отзыв. Доступ истечёт по сроку сессии.');}
  },[clear]);
  useEffect(() => {
    for (const key of ['telegram_token','telegram_user','auth_token']) localStorage.removeItem(key);
    const changed=() => {const next=getOrdinaryToken();setToken(next);if (!next) {setUser(null);setExpiresAt(0);}};
    window.addEventListener(SESSION_CHANGED,changed);
    void login();
    return () => {generation.current+=1;window.removeEventListener(SESSION_CHANGED,changed);clearOrdinarySession();};
  },[login]);
  useEffect(() => {
    if (!token) return;
    const expire=window.setTimeout(() => {clear();setError('Срок сессии истёк. Войдите снова.');},Math.max(0,expiresAt-Date.now()));
    const check=async () => {
      try {await ordinaryApi.get('/api/v1/auth/identity',{headers:{Authorization:`Bearer ${token}`}});}
      catch {if (getOrdinaryToken()===token) {clear();setError('Не удалось подтвердить доступ. Войдите снова.');}}
    };
    const interval=window.setInterval(() => {void check();},30000);
    const visible=() => {if (document.visibilityState==='visible') void check();};
    document.addEventListener('visibilitychange',visible);
    return () => {clearTimeout(expire);clearInterval(interval);document.removeEventListener('visibilitychange',visible);};
  },[token,expiresAt,clear]);
  return <TelegramAuthContext.Provider value={{user,token,isLoading,isAuthenticated:!!token&&!!user,isTelegram,error,login,logout:()=>endSession(false),logoutAll:()=>endSession(true),recover}}>{children}</TelegramAuthContext.Provider>;
};
export const useTelegramAuth=() => {const context=useContext(TelegramAuthContext);if (!context) throw new Error('useTelegramAuth must be used within TelegramAuthProvider');return context;};
